import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import api from "../../lib/api";
import { toast } from "sonner";

const SELECT_CLS =
  "mt-1 w-full rounded-md bg-slate-800 border border-slate-700 text-slate-100 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#25D366]/50";

export default function EditLeadModal({ open, lead, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: "", company: "", phone: "", email: "",
    value: 0, source_tag: "", assigned_to: "",
    pipeline_id: "", column_id: "",
  });
  const [team, setTeam] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [pipelineColumns, setPipelineColumns] = useState([]);
  const [customFieldDefs, setCustomFieldDefs] = useState([]);
  const [customValues, setCustomValues] = useState({});
  const [busy, setBusy] = useState(false);

  // Load team + pipelines + definições de campos personalizados quando o modal abre
  useEffect(() => {
    if (!open) return;
    api.get("/team").then(({ data }) => setTeam(data || [])).catch(() => {});
    api.get("/pipelines").then(({ data }) => setPipelines(Array.isArray(data) ? data : [])).catch(() => {});
    api.get("/custom-field-definitions").then(({ data }) => setCustomFieldDefs(Array.isArray(data) ? data : [])).catch(() => setCustomFieldDefs([]));
  }, [open]);

  // Populate form from lead
  useEffect(() => {
    if (lead) {
      setForm({
        name: lead.name || "",
        company: lead.company || "",
        phone: lead.phone || "",
        email: lead.email || "",
        value: lead.value || 0,
        source_tag: lead.source_tag || "",
        assigned_to: lead.assigned_to || "",
        pipeline_id: lead.pipeline_id || "",
        column_id: lead.column_id || "",
      });
      setCustomValues(lead.custom_fields || {});
    }
  }, [lead]);

  // Load columns whenever the selected pipeline changes
  useEffect(() => {
    const pid = form.pipeline_id;
    if (!pid) { setPipelineColumns([]); return; }
    api.get(`/columns?pipeline_id=${pid}`)
      .then(({ data }) => setPipelineColumns(Array.isArray(data) ? data : []))
      .catch(() => setPipelineColumns([]));
  }, [form.pipeline_id]);

  const handlePipelineChange = (newPipelineId) => {
    setForm((prev) => ({ ...prev, pipeline_id: newPipelineId, column_id: "" }));
  };

  // After columns load for a new pipeline, auto-select the "Novo Lead" column
  useEffect(() => {
    if (!form.column_id && pipelineColumns.length > 0) {
      const newLeadCol =
        pipelineColumns.find((c) => c.system_key === "new_lead") || pipelineColumns[0];
      setForm((prev) => ({ ...prev, column_id: newLeadCol?.id || "" }));
    }
  }, [pipelineColumns, form.column_id]);

  const submit = async () => {
    if (!form.name.trim()) return;
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        company: form.company.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        value: Number(form.value) || 0,
        source_tag: form.source_tag.trim() || null,
        assigned_to: form.assigned_to || null,
        pipeline_id: form.pipeline_id || null,
        column_id: form.column_id || null,
      };
      if (customFieldDefs.length > 0) {
        // Só envia chaves que têm definição ativa — o backend também filtra,
        // isso aqui é só pra não mandar lixo desnecessário.
        payload.custom_fields = customFieldDefs.reduce((acc, def) => {
          acc[def.key] = customValues[def.key] ?? "";
          return acc;
        }, {});
      }
      const { data } = await api.patch(`/leads/${lead.id}`, payload);
      toast.success("Lead atualizado");
      onSaved?.(data);
      onClose();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erro ao atualizar");
    }
    setBusy(false);
  };

  if (!lead) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="edit-lead-modal" className="bg-slate-900 border-slate-800 text-slate-100 max-w-lg max-h-[90vh] flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle style={{ fontFamily: "Outfit" }}>Editar Lead</DialogTitle>
        </DialogHeader>
        {/* min-h-0 é necessário pro overflow-y-auto funcionar dentro de um
            flex column -- sem isso o conteúdo empurra a altura do modal em
            vez de rolar internamente. Cabeçalho e rodapé ficam fixos
            (shrink-0), só esta área rola. */}
        <div className="grid grid-cols-2 gap-3 mt-2 overflow-y-auto flex-1 min-h-0 pr-1">
          <div>
            <Label className="text-xs text-slate-400">Nome *</Label>
            <Input
              data-testid="edit-lead-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="bg-slate-800 border-slate-700 mt-1"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Empresa</Label>
            <Input
              data-testid="edit-lead-company"
              value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
              className="bg-slate-800 border-slate-700 mt-1"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Telefone</Label>
            <Input
              data-testid="edit-lead-phone"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="bg-slate-800 border-slate-700 mt-1"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Email</Label>
            <Input
              data-testid="edit-lead-email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="bg-slate-800 border-slate-700 mt-1"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Valor (R$)</Label>
            <Input
              data-testid="edit-lead-value"
              type="number"
              value={form.value}
              onChange={(e) => setForm({ ...form, value: e.target.value })}
              className="bg-slate-800 border-slate-700 mt-1"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Origem</Label>
            <Input
              data-testid="edit-lead-source"
              value={form.source_tag}
              onChange={(e) => setForm({ ...form, source_tag: e.target.value })}
              className="bg-slate-800 border-slate-700 mt-1"
            />
          </div>

          {/* Campos personalizados definidos pelo tenant */}
          {customFieldDefs.length > 0 && (
            <div className="col-span-2 grid grid-cols-2 gap-3 border-t border-slate-800 pt-3">
              {customFieldDefs.map((def) => (
                <div key={def.id}>
                  <Label className="text-xs text-slate-400">{def.label}</Label>
                  {def.type === "select" ? (
                    <select
                      data-testid={`edit-lead-custom-${def.key}`}
                      value={customValues[def.key] ?? ""}
                      onChange={(e) => setCustomValues((prev) => ({ ...prev, [def.key]: e.target.value }))}
                      className={`${SELECT_CLS} mt-1`}
                    >
                      <option value="">— Selecione —</option>
                      {(def.options || []).map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  ) : (
                    <Input
                      data-testid={`edit-lead-custom-${def.key}`}
                      type={def.type === "number" ? "number" : def.type === "date" ? "date" : "text"}
                      value={customValues[def.key] ?? ""}
                      onChange={(e) => setCustomValues((prev) => ({ ...prev, [def.key]: e.target.value }))}
                      className="bg-slate-800 border-slate-700 mt-1"
                    />
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Responsável */}
          <div className="col-span-2">
            <Label className="text-xs text-slate-400">Responsável</Label>
            <p className="text-[11px] text-slate-500 mt-1">
              Sem responsável fica como lead de entrada. Ao atribuir, ele aparece para o usuário escolhido.
            </p>
            <select
              data-testid="edit-lead-assigned"
              value={form.assigned_to}
              onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}
              className={`${SELECT_CLS} mt-2`}
            >
              <option value="">Sem responsável</option>
              {team.map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>

          {/* Funil */}
          {pipelines.length > 0 && (
            <div className="col-span-2">
              <Label className="text-xs text-slate-400">Funil</Label>
              <select
                data-testid="edit-lead-pipeline"
                value={form.pipeline_id}
                onChange={(e) => handlePipelineChange(e.target.value)}
                className={SELECT_CLS}
              >
                <option value="">— Selecione o funil —</option>
                {pipelines.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}{p.is_default ? " (padrão)" : ""}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Etapa (coluna) dentro do funil selecionado */}
          {pipelineColumns.length > 0 && (
            <div className="col-span-2">
              <Label className="text-xs text-slate-400">Etapa no funil</Label>
              <select
                data-testid="edit-lead-column"
                value={form.column_id}
                onChange={(e) => setForm({ ...form, column_id: e.target.value })}
                className={SELECT_CLS}
              >
                <option value="">— Selecione a etapa —</option>
                {pipelineColumns.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 shrink-0 pt-2 border-t border-slate-800">
          <Button variant="ghost" onClick={onClose} className="text-slate-300 hover:bg-slate-800">
            Cancelar
          </Button>
          <Button
            data-testid="edit-lead-submit"
            onClick={submit}
            disabled={busy || !form.name.trim()}
            className="bg-[#25D366] hover:bg-[#1EAC52] text-slate-950 font-semibold"
          >
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
